import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronUp, ChevronDown, Eye, EyeOff, Check, X, Plus, ArrowUpRight, Link2, ArrowUp } from "lucide-react";
import { Avatar } from "@/pages/admin/saker/Avatar";
import { STRAT_STATUS, SSTATUS } from "@/pages/admin/strategi/strategiUtils";
import { tierFarge } from "@/pages/admin/eventos/eventosUtils";
import "./playbook.css";

export const TASK_STATUS = {
  inbox: { label: "Queued", dot: "#98A2B3" },
  doing: { label: "In progress", dot: "#0EA5E9" },
  waiting: { label: "Waiting", dot: "#F59E0B" },
  done: { label: "Done", dot: "#10B981" },
};

export const fmtDato = (iso, langt = false) => {
  if (!iso) return "";
  try {
    return new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString("en-GB",
      langt ? { day: "numeric", month: "long", year: "numeric" } : { day: "numeric", month: "short" });
  } catch { return ""; }
};

const iDag = () => new Date().toISOString().slice(0, 10);

/* ── Blur-save inputs (kun edit-mode) ── */
const Inp = ({ verdi, onLagre, testid, placeholder, className = "" }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  return (
    <input
      value={v}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => String(v) !== String(verdi ?? "") && onLagre(v)}
      className={`adm-input ${className}`}
      data-testid={testid}
    />
  );
};

const AutoText = ({ verdi, onLagre, rows = 4, testid, placeholder }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  return (
    <textarea
      value={v}
      rows={rows}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => String(v) !== String(verdi ?? "") && onLagre(v)}
      className="adm-input w-full"
      data-testid={testid}
    />
  );
};

const TdListe = ({ items, onLagre, tPlaceholder, dPlaceholder, testid }) => {
  const sett = (i, k, v) => onLagre(items.map((it, ix) => (ix === i ? { ...it, [k]: v } : it)));
  return (
    <div className="grid gap-2" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_1.4fr_auto] gap-2 items-start">
          <Inp verdi={it.t} placeholder={tPlaceholder} onLagre={(v) => sett(i, "t", v)} />
          <Inp verdi={it.d} placeholder={dPlaceholder} onLagre={(v) => sett(i, "d", v)} />
          <button type="button" className="adm-btn-ghost adm-btn-sm mt-1" onClick={() => onLagre(items.filter((_, ix) => ix !== i))} aria-label="Fjern">
            <X size={12} />
          </button>
        </div>
      ))}
      <button type="button" className="adm-btn-ghost adm-btn-sm justify-self-start inline-flex items-center gap-1" onClick={() => onLagre([...items, { t: "", d: "" }])}>
        <Plus size={12} /> Legg til
      </button>
    </div>
  );
};

const Seksjon = ({ s, nr, edit, onPatch, onFlytt, onKopier, children }) => (
  <section id={`pb-anker-${s.key}`} className={`pb-sek pb-reveal border-t border-neutral-200 ${s.hidden ? "skjult" : ""}`} data-testid={`pb-sek-${s.key}`}>
    <div className="flex items-baseline gap-4 mb-8">
      <span className="pb-idx">{nr}</span>
      {edit ? (
        <Inp verdi={s.title} onLagre={(v) => v.trim() && onPatch({ title: v })} className="max-w-[340px] font-semibold" testid={`pb-tittel-${s.key}`} />
      ) : (
        <h2 className="pb-sek-tittel text-neutral-900">{s.title}</h2>
      )}
      <span className="flex-1 border-t border-neutral-200 translate-y-[-3px]" />
      {!edit && onKopier && (
        <button
          type="button"
          onClick={onKopier}
          className="pb-kopier text-neutral-400 hover:text-neutral-800 shrink-0"
          title="Kopier lenke til seksjonen"
          aria-label={`Kopier lenke til ${s.title}`}
          data-testid={`pb-kopier-${s.key}`}
        >
          <Link2 size={13} />
        </button>
      )}
      {edit && (
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" className="adm-btn-ghost adm-btn-sm" onClick={() => onFlytt(-1)} aria-label="Flytt opp" data-testid={`pb-opp-${s.key}`}><ChevronUp size={13} /></button>
          <button type="button" className="adm-btn-ghost adm-btn-sm" onClick={() => onFlytt(1)} aria-label="Flytt ned"><ChevronDown size={13} /></button>
          <button type="button" className="adm-btn-ghost adm-btn-sm" onClick={() => onPatch({ hidden: !s.hidden })} title={s.hidden ? "Vis seksjonen" : "Skjul seksjonen"} data-testid={`pb-skjul-${s.key}`}>
            {s.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        </div>
      )}
    </div>
    {edit ? (
      <div className="mb-4"><Inp verdi={s.note} placeholder="Kort notat (valgfritt — vises under seksjonstittelen)" onLagre={(v) => onPatch({ note: v })} testid={`pb-note-${s.key}`} /></div>
    ) : (
      s.note && <p className="text-[12.5px] text-neutral-400 italic mb-6 -mt-3">{s.note}</p>
    )}
    {children}
  </section>
);

function MsTaskSok({ tasks, onVelg, testid }) {
  const [sok, setSok] = useState("");
  const treff = useMemo(() => {
    const q = sok.trim().toLowerCase();
    if (q.length < 2) return [];
    return tasks.filter((t) => (t.title || "").toLowerCase().includes(q)).slice(0, 5);
  }, [sok, tasks]);
  return (
    <div className="max-w-md">
      <input
        value={sok}
        onChange={(e) => setSok(e.target.value)}
        className="adm-input"
        placeholder="Koble saker — søk på tittel …"
        data-testid={testid}
      />
      {treff.length > 0 && (
        <div className="mt-1 border border-neutral-200 divide-y divide-neutral-100">
          {treff.map((t) => (
            <button key={t.id} type="button" onClick={() => { onVelg(t); setSok(""); }} className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] text-neutral-700 hover:bg-neutral-50" data-testid={`pb-ms-treff-${t.id}`}>
              <span className="w-1 h-1 rounded-full shrink-0" style={{ background: (TASK_STATUS[t.status] || TASK_STATUS.inbox).dot }} />
              <span className="truncate">{t.title}</span>
              <span className="pb-mono text-[9px] uppercase tracking-[0.1em] text-neutral-400 ml-auto shrink-0">{(TASK_STATUS[t.status] || TASK_STATUS.inbox).label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LinkSok({ linkables, onVelg, testid }) {
  const [sok, setSok] = useState("");
  const treff = useMemo(() => {
    const q = sok.trim().toLowerCase();
    if (q.length < 2) return [];
    return linkables.filter((l) => l.label.toLowerCase().includes(q)).slice(0, 6);
  }, [sok, linkables]);
  return (
    <div className="mt-3 max-w-md">
      <input
        value={sok}
        onChange={(e) => setSok(e.target.value)}
        className="adm-input"
        placeholder="Søk targets og speakers for å koble …"
        data-testid={testid}
      />
      {treff.length > 0 && (
        <div className="mt-1 border border-neutral-200 divide-y divide-neutral-100">
          {treff.map((l) => (
            <button key={`${l.kind}:${l.id}`} type="button" onClick={() => { onVelg(l); setSok(""); }} className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] text-neutral-700 hover:bg-neutral-50" data-testid={`pb-treff-${l.id}`}>
              <span className="truncate">{l.label}</span>
              <span className="pb-mono text-[9px] uppercase tracking-[0.1em] text-neutral-400 ml-auto shrink-0">{l.meta}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══ Selve dokumentet — delt mellom admin og offentlig visning ═══════════ */
export default function PlaybookDokument({
  data,
  edit = false,
  interaktiv = true,
  delt = false,
  verktoy = null,
  onPatchSection = () => {},
  onPatchStrategi = () => {},
  onFlytt = () => {},
  onKopier = null,
}) {
  const nav = useNavigate();
  const [aktivSek, setAktivSek] = useState(null);
  const [fremdrift, setFremdrift] = useState(0);
  const [taskSort, setTaskSort] = useState({ key: null, dir: 1 });

  const sortert = useMemo(() => [...(data?.sections || [])].sort((a, b) => a.order - b.order), [data]);
  const synlige = useMemo(() => (edit ? sortert : sortert.filter((s) => !s.hidden)), [sortert, edit]);
  const byMember = useMemo(() => Object.fromEntries((data?.members || []).map((m) => [m.id, m])), [data]);

  // Scroll-koreografi + scrollspy
  useEffect(() => {
    const seksjoner = Array.from(document.querySelectorAll(".pb-reveal"));
    const redusert = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (redusert) seksjoner.forEach((el) => el.classList.add("in"));
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.08, rootMargin: "0px 0px -4% 0px" }
    );
    const spy = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setAktivSek(e.target.id.replace("pb-anker-", "")); }),
      { rootMargin: "-20% 0px -65% 0px" }
    );
    seksjoner.forEach((el) => { if (!redusert) io.observe(el); spy.observe(el); });
    return () => { io.disconnect(); spy.disconnect(); };
  }, [data, edit, synlige.length]);

  // Lesefremdrift
  useEffect(() => {
    let raf = 0;
    const oppdater = () => {
      raf = 0;
      const h = document.documentElement;
      const max = h.scrollHeight - window.innerHeight;
      setFremdrift(max > 0 ? Math.min(100, Math.max(0, (window.scrollY / max) * 100)) : 0);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(oppdater); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    oppdater();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const scrollTil = (key) => {
    document.getElementById(`pb-anker-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sporMus = (e) => {
    const kort = e.target.closest?.(".pb-kort");
    if (!kort) return;
    const r = kort.getBoundingClientRect();
    kort.style.setProperty("--mx", `${e.clientX - r.left}px`);
    kort.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const ev = data.event || {};
  const dato = ev.eventDate ? fmtDato(ev.eventDate, true) : "13 April 2027";
  const venue = (ev.venue || "Grieghallen, Bergen").split(",")[0].trim();
  const apneValg = (data.strategy || []).filter((c) => c.status === "open").length;
  const dagerIgjen = Math.max(0, Math.ceil((new Date(`${ev.eventDate || "2027-04-13"}T09:00:00Z`).getTime() - Date.now()) / 86400000));

  /* ── Renderere per seksjonstype ── */
  const rVision = (s) =>
    edit ? (
      <AutoText verdi={s.data.body} rows={5} onLagre={(v) => onPatchSection(s.id, { data: { body: v } })} testid="pb-vision-body" />
    ) : (
      <div className="max-w-2xl">
        {String(s.data.body || "").split(/\n\n+/).map((p, i) => (
          <p key={i} className={i === 0
            ? "text-[19px] sm:text-[24px] leading-[1.52] sm:leading-[1.45] font-medium text-neutral-900 tracking-[-0.012em]"
            : "mt-5 text-[14px] sm:text-[15px] leading-[1.75] text-neutral-500"}>{p}</p>
        ))}
      </div>
    );

  const rBuilding = (s) =>
    edit ? (
      <TdListe items={s.data.cards || []} onLagre={(v) => onPatchSection(s.id, { data: { cards: v } })} tPlaceholder="Statement" dPlaceholder="Kort forklaring" testid="pb-building-edit" />
    ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
        {(s.data.cards || []).map((c, i) => (
          <div key={c.t} className={`border-t border-neutral-300 pt-4 ${i === 0 ? "sm:col-span-2" : ""}`}>
            <span className="pb-mono text-[9.5px] text-neutral-300">0{i + 1}</span>
            <p className={`font-semibold text-neutral-900 leading-snug mt-1.5 ${i === 0 ? "text-[21px] tracking-[-0.01em]" : "text-[15px]"}`}>{c.t}</p>
            <p className="text-[12.5px] text-neutral-500 mt-1.5 leading-relaxed">{c.d}</p>
          </div>
        ))}
      </div>
    );

  const rFormula = (s) =>
    edit ? (
      <div className="grid gap-3">
        <TdListe items={s.data.blocks || []} onLagre={(v) => onPatchSection(s.id, { data: { blocks: v } })} tPlaceholder="Byggekloss" dPlaceholder="Kort forklaring" testid="pb-formula-edit" />
        <label className="adm-label max-w-[340px]">Resultat
          <Inp verdi={s.data.result} onLagre={(v) => onPatchSection(s.id, { data: { result: v } })} testid="pb-formula-result" />
        </label>
      </div>
    ) : (
      <div className="max-w-xl" data-testid="pb-formula">
        {(s.data.blocks || []).map((b, i) => (
          <div key={b.t}>
            {i > 0 && <p className="pb-fplus">+</p>}
            <div className="pb-fblokk">
              <span className="fidx">0{i + 1}</span>
              <p className="pb-mono text-[12px] font-semibold uppercase tracking-[0.14em] text-neutral-900">{b.t}</p>
              <p className="text-[12.5px] text-neutral-500 mt-1">{b.d}</p>
            </div>
          </div>
        ))}
        <div className="pb-fresult">
          <span className="pb-mono text-[18px] text-amber-600 font-semibold">=</span>
          <p className="text-[32px] font-bold tracking-[-0.018em] uppercase text-neutral-900" style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif' }}>{s.data.result}</p>
        </div>
      </div>
    );

  const rChoices = () => (
    <div className="grid gap-0" data-testid="pb-choices">
      <div className="flex flex-wrap gap-2 mb-6">
        {STRAT_STATUS.map((st) => {
          const n = (data.strategy || []).filter((c) => c.status === st.k).length;
          if (!n) return null;
          return (
            <span key={st.k} className="pb-mono text-[9.5px] uppercase tracking-[0.14em] rounded-full px-3 py-1.5 inline-flex items-center gap-2" style={{ color: st.dot, background: `${st.dot}1c` }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} />
              {n} {st.label}
            </span>
          );
        })}
      </div>
      {(data.strategy || []).map((c) => {
        const st = SSTATUS[c.status] || SSTATUS.direction;
        return (
          <div key={c.id} className="border-t border-neutral-200 py-4 group grid sm:grid-cols-[150px_1fr] gap-x-6 gap-y-1 items-baseline">
            <span className="pb-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5 justify-self-start" style={{ color: st.dot, background: `${st.dot}1c` }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: st.dot }} />
              {st.label}
            </span>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                {interaktiv ? (
                  <button type="button" onClick={() => nav("/admin/strategy")} className="text-[15px] font-semibold text-neutral-900 hover:underline underline-offset-4 decoration-neutral-300 text-left">
                    {c.title}
                  </button>
                ) : (
                  <p className="text-[15px] font-semibold text-neutral-900">{c.title}</p>
                )}
                {edit && (
                  <select
                    value={c.status}
                    onChange={(e) => onPatchStrategi(c.id, e.target.value)}
                    className="adm-input !w-auto !py-1 !text-[11.5px] ml-auto"
                    data-testid={`pb-choice-status-${c.id}`}
                  >
                    {STRAT_STATUS.map((x) => <option key={x.k} value={x.k}>{x.label}</option>)}
                  </select>
                )}
              </div>
              <p className="text-[13px] text-neutral-500 mt-1 max-w-2xl leading-relaxed">{c.statement}</p>
            </div>
          </div>
        );
      })}
      {interaktiv && (
        <button type="button" onClick={() => nav("/admin/strategy")} className="justify-self-start mt-4 inline-flex items-center gap-1.5 pb-mono text-[10px] uppercase tracking-[0.16em] text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="pb-choices-link">
          Live from Strategy Board <ArrowUpRight size={11} />
        </button>
      )}
    </div>
  );

  const rPartnership = (s) =>
    edit ? (
      <div className="grid gap-4">
        <TdListe items={s.data.model || []} onLagre={(v) => onPatchSection(s.id, { data: { model: v } })} tPlaceholder="Modell-element" dPlaceholder="Kort forklaring" testid="pb-partner-edit" />
        <label className="adm-label">Roller (kommaseparert)
          <Inp verdi={(s.data.roles || []).join(", ")} onLagre={(v) => onPatchSection(s.id, { data: { roles: v.split(",").map((x) => x.trim()).filter(Boolean) } })} testid="pb-partner-roller" />
        </label>
        <label className="adm-label">Statement
          <Inp verdi={s.data.statement} onLagre={(v) => onPatchSection(s.id, { data: { statement: v } })} testid="pb-partner-statement" />
        </label>
      </div>
    ) : (
      <div>
        <div className="grid sm:grid-cols-3 gap-6 max-w-3xl">
          {(s.data.model || []).map((m2, i) => (
            <div key={m2.t} className="border-t border-neutral-200 pt-3.5">
              <span className="pb-mono text-[9.5px] text-neutral-300">0{i + 1}</span>
              <p className="text-[14.5px] font-semibold text-neutral-900 mt-1 leading-snug">{m2.t}</p>
              <p className="text-[12.5px] text-neutral-500 mt-1.5 leading-relaxed">{m2.d}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-8">
          {(s.data.roles || []).map((r) => (
            <span key={r} className="pb-mono text-[10px] uppercase tracking-[0.14em] border border-neutral-200 px-3 py-2 text-neutral-600">{r}</span>
          ))}
        </div>
        {s.data.statement && (
          <p className="mt-9 text-[23px] font-semibold text-neutral-900 tracking-[-0.015em]" style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif' }}>{s.data.statement}</p>
        )}
      </div>
    );

  const speakerChip = (kind, id) => {
    const t = kind === "target" ? data.targetLookup?.[id] : null;
    const p = kind === "speaker" ? data.participationLookup?.[id] : null;
    if (!t && !p) return null;
    return {
      navn: t ? t.name : p.name,
      meta: t ? `Target · Tier ${t.tier || "?"}` : `Speaker · ${p.stage || ""}`,
      farge: t ? tierFarge(t.tier) : "#10B981",
    };
  };

  const rSpeakers = (s) => {
    const lagreGrupper = (groups) => onPatchSection(s.id, { data: { groups } });
    return (
      <div className="grid gap-8" data-testid="pb-speakers">
        {(s.data.groups || []).map((g, gi) => {
          const valgt = new Set([...(g.targetIds || []).map((i) => `target:${i}`), ...(g.participationIds || []).map((i) => `speaker:${i}`)]);
          return (
            <div key={g.id} className="border-t border-neutral-200 pt-4">
              {edit ? (
                <div className="grid gap-2 mb-3 max-w-xl">
                  <Inp verdi={g.title} onLagre={(v) => lagreGrupper(s.data.groups.map((x, ix) => (ix === gi ? { ...x, title: v } : x)))} testid={`pb-gruppe-tittel-${gi}`} />
                  <Inp verdi={g.note} placeholder="Kort status/notat" onLagre={(v) => lagreGrupper(s.data.groups.map((x, ix) => (ix === gi ? { ...x, note: v } : x)))} />
                </div>
              ) : (
                <>
                  <p className="text-[15px] font-semibold text-neutral-900">{g.title}</p>
                  {g.note && <p className="text-[12.5px] text-neutral-500 mt-0.5">{g.note}</p>}
                </>
              )}
              <div className="flex flex-wrap gap-2 mt-3.5">
                {[...(g.targetIds || []).map((id) => ["target", id]), ...(g.participationIds || []).map((id) => ["speaker", id])].map(([kind, id]) => {
                  const c = speakerChip(kind, id);
                  if (!c) return null;
                  const initialer = c.navn.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                  return (
                    <span key={`${kind}:${id}`} className="inline-flex items-center gap-2.5 py-1 pr-5" data-testid={`pb-chip-${id}`}>
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0" style={{ color: c.farge, background: `${c.farge}1f`, boxShadow: `inset 0 0 0 1.5px ${c.farge}55` }}>{initialer}</span>
                      {interaktiv ? (
                        <button type="button" onClick={() => nav("/admin/speakers")} className="text-[12.5px] font-medium text-neutral-800 hover:text-neutral-900">
                          {c.navn}
                        </button>
                      ) : (
                        <span className="text-[12.5px] font-medium text-neutral-800">{c.navn}</span>
                      )}
                      <span className="pb-mono text-[8.5px] uppercase tracking-[0.1em] text-neutral-400">{c.meta}</span>
                      {edit && (
                        <button
                          type="button"
                          onClick={() => lagreGrupper(s.data.groups.map((x, ix) => (ix === gi ? {
                            ...x,
                            targetIds: kind === "target" ? x.targetIds.filter((i) => i !== id) : x.targetIds,
                            participationIds: kind === "speaker" ? x.participationIds.filter((i) => i !== id) : x.participationIds,
                          } : x)))}
                          className="text-neutral-300 hover:text-neutral-700"
                          aria-label="Fjern"
                        ><X size={11} /></button>
                      )}
                    </span>
                  );
                })}
              </div>
              {edit && (
                <LinkSok
                  linkables={(data.linkables || []).filter((l) => !valgt.has(`${l.kind}:${l.id}`))}
                  onVelg={(l) => lagreGrupper(s.data.groups.map((x, ix) => (ix === gi ? {
                    ...x,
                    targetIds: l.kind === "target" ? [...x.targetIds, l.id] : x.targetIds,
                    participationIds: l.kind === "speaker" ? [...x.participationIds, l.id] : x.participationIds,
                  } : x)))}
                  testid={`pb-sok-${gi}`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const rPriorities = (s) => (
    <div data-testid="pb-priorities">
      {(data.priorities || []).length === 0 ? (
        <p className="text-[13px] text-neutral-400">
          {delt ? "No strategic priorities are published right now." : "Ingen saker er merket som strategisk prioritet ennå. Gi saker etiketten «Strategic Priority» i Saker, så vises de her automatisk."}
        </p>
      ) : (
        <div className="divide-y divide-neutral-100">
          {(data.priorities || []).map((t) => {
            const st = TASK_STATUS[t.status] || TASK_STATUS.inbox;
            const eier = t.assigneeName
              ? { name: t.assigneeName, color: t.assigneeColor, id: t.id }
              : byMember[t.assigneeId];
            const forsinkelse = t.dueDate && t.dueDate < iDag();
            const Rot = interaktiv ? "button" : "div";
            return (
              <Rot
                key={t.id}
                {...(interaktiv ? { type: "button", onClick: () => nav("/admin/saker") } : {})}
                className={`w-full flex items-center flex-wrap gap-x-3.5 gap-y-1.5 py-4 text-left ${interaktiv ? "group transition-colors" : ""}`}
                data-testid={`pb-pri-${t.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.dot }} />
                <p className={`text-[14.5px] font-medium text-neutral-900 flex-1 min-w-[200px] ${interaktiv ? "group-hover:underline underline-offset-4 decoration-neutral-300" : ""}`}>{t.title}</p>
                {t.priority === 1 && <span className="pb-mono text-[8.5px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5" style={{ color: "#EF5350", background: "#EF53501c" }}>P1</span>}
                <span className="ml-auto flex items-center gap-4 shrink-0 text-[11px] text-neutral-400">
                  {eier && <span className="inline-flex items-center gap-1.5"><Avatar person={eier} size={16} />{(eier.name || "").split(" ")[0]}</span>}
                  {t.dueDate && <span className={`pb-mono ${forsinkelse ? "text-red-500" : ""}`}>{fmtDato(t.dueDate)}</span>}
                  <span className="pb-mono text-[9.5px] uppercase tracking-[0.12em] rounded-full px-2.5 py-1" style={{ color: st.dot, background: `${st.dot}1c` }}>{st.label}</span>
                </span>
              </Rot>
            );
          })}
        </div>
      )}
      {edit && (
        <div className="mt-4 flex items-center gap-3">
          <label className="adm-label !mb-0">Maks antall
            <Inp verdi={String(s.data.limit || 8)} onLagre={(v) => onPatchSection(s.id, { data: { limit: parseInt(v, 10) || 8 } })} className="!w-20" testid="pb-pri-limit" />
          </label>
          <p className="text-[11.5px] text-neutral-400">Trekker inn eksisterende saker med etiketten «Strategic Priority» / «Strategi» — ingen duplisering.</p>
        </div>
      )}
    </div>
  );

  const rTasks = (s) => {
    const inst = s.data || {};
    const filter = inst.filter || "all";
    const visDone = !!inst.showDone;
    const limit = inst.limit || 15;
    let rader = data.tasks || [];
    if (filter === "strategic") rader = rader.filter((t) => (t.labels || []).some((lb) => String(lb).toLowerCase().includes("strateg")));
    if (!visDone) rader = rader.filter((t) => t.status !== "done");
    rader = rader.slice(0, limit);

    // Sortérbare kolonner (Confluence-stil) — klikk toggler asc/desc
    const eierNavn = (t) => (t.assigneeName || byMember[t.assigneeId]?.name || "").toLowerCase();
    const statusOrden = { doing: 0, waiting: 1, inbox: 2, done: 3 };
    const SORT = {
      title: (t) => (t.title || "").toLowerCase(),
      owner: (t) => [eierNavn(t) === "", eierNavn(t)],
      due: (t) => [t.dueDate == null, t.dueDate || "9999"],
      pri: (t) => t.priority || 2,
      status: (t) => statusOrden[t.status] ?? 2,
    };
    if (taskSort.key && SORT[taskSort.key]) {
      const f = SORT[taskSort.key];
      rader = [...rader].sort((a, b) => {
        const va = f(a);
        const vb = f(b);
        const cmp = va < vb ? -1 : va > vb ? 1 : 0;
        return cmp * taskSort.dir;
      });
    }
    const sorter = (key) => setTaskSort((s2) => (s2.key === key ? { key, dir: -s2.dir } : { key, dir: 1 }));
    const kolKnapp = (k, tekst) => (
      <button type="button" onClick={() => sorter(k)} className="text-left uppercase inline-flex items-center gap-1 hover:text-neutral-700 transition-colors" data-testid={`pb-tasks-sort-${k}`}>
        {tekst}
        {taskSort.key === k && <span className="text-amber-600">{taskSort.dir === 1 ? "▲" : "▼"}</span>}
      </button>
    );
    const forfalt = rader.filter((t) => t.dueDate && t.dueDate < iDag() && t.status !== "done").length;

    return (
      <div data-testid="pb-tasks">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="pb-mono text-[9.5px] uppercase tracking-[0.14em] rounded-full px-3 py-1.5 border border-neutral-200 text-neutral-500">
            {rader.length} {rader.length === 1 ? "task" : "tasks"}
          </span>
          {forfalt > 0 && (
            <span className="pb-mono text-[9.5px] uppercase tracking-[0.14em] rounded-full px-3 py-1.5 inline-flex items-center gap-2" style={{ color: "#EF5350", background: "#EF53501c" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#EF5350" }} />
              {forfalt} overdue
            </span>
          )}
          {filter === "strategic" && (
            <span className="pb-mono text-[9.5px] uppercase tracking-[0.14em] rounded-full px-3 py-1.5 border border-neutral-200 text-neutral-500">Strategic only</span>
          )}
        </div>

        {rader.length === 0 ? (
          <p className="text-[13px] text-neutral-400">{delt ? "No open tasks right now." : "Ingen saker å vise — juster filteret i edit-mode, eller opprett saker i Saker."}</p>
        ) : (
          <div>
            <div className="pb-tabellrad pb-tabellhode">
              {kolKnapp("title", "Task")}
              {kolKnapp("owner", "Owner")}
              {kolKnapp("due", "Due")}
              {kolKnapp("pri", "Pri")}
              {kolKnapp("status", "Status")}
            </div>
            <div className="divide-y divide-neutral-100">
              {rader.map((t) => {
                const st = TASK_STATUS[t.status] || TASK_STATUS.inbox;
                const eier = t.assigneeName
                  ? { name: t.assigneeName, color: t.assigneeColor, id: t.id }
                  : byMember[t.assigneeId];
                const over = t.dueDate && t.dueDate < iDag() && t.status !== "done";
                const Rot = interaktiv ? "button" : "div";
                return (
                  <Rot
                    key={t.id}
                    {...(interaktiv ? { type: "button", onClick: () => nav("/admin/saker") } : {})}
                    className={`pb-tabellrad w-full text-left ${interaktiv ? "group" : ""}`}
                    data-testid={`pb-task-${t.id}`}
                  >
                    <span className="min-w-0">
                      <span className={`block text-[13.5px] font-medium leading-snug ${t.status === "done" ? "text-neutral-400 line-through" : "text-neutral-900"} ${interaktiv ? "group-hover:underline underline-offset-4 decoration-neutral-300" : ""}`}>{t.title}</span>
                      {(t.labels || []).length > 0 && (
                        <span className="hidden sm:flex flex-wrap gap-1.5 mt-1.5">
                          {t.labels.slice(0, 3).map((lb) => (
                            <span key={lb} className="pb-mono text-[8.5px] uppercase tracking-[0.1em] border border-neutral-200 rounded px-1.5 py-0.5 text-neutral-400">{lb}</span>
                          ))}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-1.5 text-[11.5px] text-neutral-500 min-w-0">
                      {eier ? (<><Avatar person={eier} size={17} /><span className="truncate">{(eier.name || "").split(" ")[0]}</span></>) : <span className="text-neutral-300">—</span>}
                    </span>
                    <span className={`pb-mono text-[10.5px] ${over ? "text-red-500 font-semibold" : "text-neutral-500"}`}>
                      {t.dueDate ? fmtDato(t.dueDate) : "—"}
                    </span>
                    <span>
                      {t.priority === 1
                        ? <span className="pb-mono text-[8.5px] uppercase tracking-[0.1em] rounded-full px-1.5 py-0.5" style={{ color: "#EF5350", background: "#EF53501c" }}>P1</span>
                        : <span className="pb-mono text-[10px] text-neutral-400">P{t.priority || 2}</span>}
                    </span>
                    <span className="pb-mono text-[9px] uppercase tracking-[0.1em] rounded-full px-2 py-1 justify-self-start inline-flex items-center gap-1.5" style={{ color: st.dot, background: `${st.dot}1c` }}>
                      <span className="w-1 h-1 rounded-full" style={{ background: st.dot }} />
                      {st.label}
                    </span>
                  </Rot>
                );
              })}
            </div>
          </div>
        )}

        {edit && (
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="adm-label !mb-0">Filter
              <select value={filter} onChange={(e) => onPatchSection(s.id, { data: { filter: e.target.value } })} className="adm-input" data-testid="pb-tasks-filter">
                <option value="all">Alle saker</option>
                <option value="strategic">Kun strategiske</option>
              </select>
            </label>
            <label className="adm-label !mb-0">Maks antall
              <Inp verdi={String(limit)} onLagre={(v) => onPatchSection(s.id, { data: { limit: parseInt(v, 10) || 15 } })} className="!w-20" testid="pb-tasks-limit" />
            </label>
            <label className="flex items-center gap-2 text-[12px] text-neutral-500 cursor-pointer mt-4">
              <input type="checkbox" checked={visDone} onChange={(e) => onPatchSection(s.id, { data: { showDone: e.target.checked } })} data-testid="pb-tasks-done" />
              Vis ferdige
            </label>
            <p className="text-[11.5px] text-neutral-400 mt-4">Live fra Saker — ingen duplisering.</p>
          </div>
        )}
      </div>
    );
  };

  const msFremdrift = (it) => {
    if (it.progress) return it.progress;
    const ids = it.taskIds || [];
    if (!ids.length) return null;
    const kjent = (data.tasks || []).filter((t) => ids.includes(t.id));
    return { done: kjent.filter((t) => t.status === "done").length, total: ids.length };
  };

  const rMilestones = (s) => {
    const items = s.data.items || [];
    const lagre = (nye) => onPatchSection(s.id, { data: { items: nye } });
    if (edit) {
      return (
        <div className="grid gap-5" data-testid="pb-milestones-edit">
          {items.map((it, i) => {
            const ids = it.taskIds || [];
            const bytt = (retning) => {
              const j = i + retning;
              if (j < 0 || j >= items.length) return;
              const nye = [...items];
              [nye[i], nye[j]] = [nye[j], nye[i]];
              lagre(nye);
            };
            return (
              <div key={i} className="border border-neutral-200 rounded-xl p-4 grid gap-2.5">
                <div className="grid grid-cols-[auto_1fr_1.4fr_auto] gap-2 items-start">
                  <span className="pb-idx mt-1.5">{String(i + 1).padStart(2, "0")}</span>
                  <Inp verdi={it.t} placeholder="Fase" onLagre={(v) => lagre(items.map((x, ix) => (ix === i ? { ...x, t: v } : x)))} />
                  <Inp verdi={it.d} placeholder="Hva skjer" onLagre={(v) => lagre(items.map((x, ix) => (ix === i ? { ...x, d: v } : x)))} />
                  <span className="flex items-center gap-1 mt-1">
                    <button type="button" className="adm-btn-ghost adm-btn-sm" onClick={() => bytt(-1)} aria-label="Flytt venstre" data-testid={`pb-ms-opp-${i}`}><ChevronUp size={12} /></button>
                    <button type="button" className="adm-btn-ghost adm-btn-sm" onClick={() => bytt(1)} aria-label="Flytt høyre"><ChevronDown size={12} /></button>
                    <button type="button" className="adm-btn-ghost adm-btn-sm" onClick={() => lagre(items.filter((_, ix) => ix !== i))} aria-label="Fjern"><X size={12} /></button>
                  </span>
                </div>
                {ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {ids.map((id) => {
                      const t = (data.tasks || []).find((x) => x.id === id);
                      return (
                        <span key={id} className="inline-flex items-center gap-1.5 text-[11px] border border-neutral-200 rounded px-2 py-1 text-neutral-600">
                          <span className="w-1 h-1 rounded-full" style={{ background: (TASK_STATUS[t?.status] || TASK_STATUS.inbox).dot }} />
                          <span className="truncate max-w-[220px]">{t?.title || "Ukjent sak"}</span>
                          <button type="button" onClick={() => lagre(items.map((x, ix) => (ix === i ? { ...x, taskIds: ids.filter((y) => y !== id) } : x)))} className="text-neutral-300 hover:text-neutral-700" aria-label="Fjern kobling"><X size={10} /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <MsTaskSok
                  tasks={(data.tasks || []).filter((t) => !ids.includes(t.id))}
                  onVelg={(t) => lagre(items.map((x, ix) => (ix === i ? { ...x, taskIds: [...ids, t.id] } : x)))}
                  testid={`pb-ms-sok-${i}`}
                />
              </div>
            );
          })}
          <button type="button" className="adm-btn-ghost adm-btn-sm justify-self-start inline-flex items-center gap-1" onClick={() => lagre([...items, { t: "", d: "", taskIds: [] }])}>
            <Plus size={12} /> Legg til milestone
          </button>
        </div>
      );
    }
    const fps = items.map((x) => msFremdrift(x));
    const erFerdig = (fp) => !!fp && fp.total > 0 && fp.done === fp.total;
    const aktivIdx = items.findIndex((_, ix) => !erFerdig(fps[ix]));
    return (
      <div className="pb-tidslinje" data-testid="pb-milestones">
        {items.map((m2, i) => {
          const fp = fps[i];
          // Fase-status avledet av task-fremdrift: alt før første uferdige = ferdig.
          const fase = aktivIdx === -1 || i < aktivIdx ? "done" : i === aktivIdx ? "active" : "upcoming";
          const kjente = (m2.taskIds || [])
            .map((id) => (data.tasks || []).find((t) => t.id === id))
            .filter(Boolean);
          const segmenter = kjente.length
            ? [...kjente].sort((a, b) => (a.status === "done" ? -1 : 1) - (b.status === "done" ? -1 : 1))
                .map((t) => (TASK_STATUS[t.status] || TASK_STATUS.inbox))
            : fp && fp.total > 0
              ? Array.from({ length: fp.total }, (_, x) => (x < fp.done ? TASK_STATUS.done : null))
              : [];
          return (
            <div key={m2.t} className={`pb-node ${fase === "done" ? "ferdig" : ""}`}>
              <span className={`pb-dot ${fase === "done" ? "ok" : fase === "active" ? "naa" : ""}`} />
              <p className={`pb-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] ${fase === "active" ? "text-amber-600" : fase === "upcoming" ? "text-neutral-400" : "text-neutral-900"}`}>{m2.t}</p>
              <p className="text-[12px] text-neutral-500 mt-1.5 leading-snug pr-2">{m2.d}</p>
              <p
                className={`pb-mono text-[8.5px] mt-2 uppercase tracking-[0.16em] ${fase === "done" ? "text-emerald-600" : fase === "active" ? "text-amber-600" : "text-neutral-400"}`}
                data-testid={`pb-ms-fase-${i}`}
              >
                {fase === "done" ? "Complete ✓" : fase === "active" ? "Active phase" : "Upcoming"}
                {fp && fp.total > 0 && <span className="text-neutral-400"> · {fp.done}/{fp.total} tasks</span>}
              </p>
              {fp && fp.total > 0 && (
                <div className="mt-2 pr-4" data-testid={`pb-ms-progress-${i}`}>
                  <div className="pb-seg">
                    {segmenter.map((st, x) => (
                      <span key={x} style={st ? { background: st.dot } : undefined} />
                    ))}
                  </div>
                </div>
              )}
              {interaktiv && kjente.length > 0 && (
                <div className="pb-pop" data-testid={`pb-ms-pop-${i}`}>
                  <p className="pb-mono text-[8px] uppercase tracking-[0.22em] text-neutral-400 mb-2">Linked tasks</p>
                  <div className="grid gap-1.5">
                    {kjente.map((t) => {
                      const st = TASK_STATUS[t.status] || TASK_STATUS.inbox;
                      return (
                        <button key={t.id} type="button" onClick={() => nav("/admin/saker")} className="flex items-center gap-2 text-left group">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.dot }} />
                          <span className={`text-[11.5px] leading-snug truncate group-hover:underline underline-offset-2 ${t.status === "done" ? "text-neutral-400 line-through" : "text-neutral-700"}`}>{t.title}</span>
                          {t.dueDate && <span className="pb-mono text-[8.5px] text-neutral-400 ml-auto shrink-0">{fmtDato(t.dueDate)}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const rSuccess = (s) =>
    edit ? (
      <div className="grid gap-2">
        {(s.data.items || []).map((it, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] gap-2">
            <Inp verdi={it} onLagre={(v) => onPatchSection(s.id, { data: { items: s.data.items.map((x, ix) => (ix === i ? v : x)).filter(Boolean) } })} />
            <button type="button" className="adm-btn-ghost adm-btn-sm" onClick={() => onPatchSection(s.id, { data: { items: s.data.items.filter((_, ix) => ix !== i) } })} aria-label="Fjern"><X size={12} /></button>
          </div>
        ))}
        <button type="button" className="adm-btn-ghost adm-btn-sm justify-self-start inline-flex items-center gap-1" onClick={() => onPatchSection(s.id, { data: { items: [...(s.data.items || []), "Nytt suksesskriterium"] } })}>
          <Plus size={12} /> Legg til
        </button>
      </div>
    ) : (
      <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3.5" data-testid="pb-success">
        {(s.data.items || []).map((it) => (
          <div key={it} className="flex items-start gap-3">
            <span className="pb-tick w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-[1px]">
              <Check size={11} className="text-amber-600" strokeWidth={2.75} />
            </span>
            <p className="text-[14px] text-neutral-800 leading-relaxed">{it}</p>
          </div>
        ))}
      </div>
    );

  const rEconomics = (s) => {
    const ec = data.economics;
    if (!ec) return null;
    const fm = (n) => (Math.abs(Number(n) || 0) >= 1e6 ? `${((Number(n) || 0) / 1e6).toFixed(2)}m` : `${Math.round((Number(n) || 0) / 1000)}k`);
    const dekning = Math.min(100, ((ec.partnerSigned || 0) / Math.max(1, ec.partnerTarget || 1)) * 100);
    return (
      <div data-testid="pb-economics">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-6">
          {[
            ["Revenue · Base", fm(ec.revenue), null],
            ["Costs incl. contingency", fm(ec.costs), null],
            ["Forecast profit", `${ec.profit < 0 ? "−" : "+"}${fm(Math.abs(ec.profit))}`, ec.profit < 0 ? "#EF5350" : "#059669"],
            ["Margin", `${ec.margin}%`, null],
          ].map(([k, v, farge]) => (
            <div key={k}>
              <p className="pb-mono text-[8.5px] uppercase tracking-[0.22em] text-neutral-400">{k}</p>
              <p className="text-[30px] font-bold tracking-[-0.015em] mt-1.5 leading-none" style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif', color: farge || undefined }}>{v}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-7" data-testid="pb-economics-split">
          {[
            ["Physical", ec.split?.physical], ["Partners", ec.split?.sponsors],
            ["Digital", ec.split?.digital], ["Dinner", ec.split?.dinner],
          ].map(([k, v]) => (
            <span key={k} className="pb-mono text-[9.5px] uppercase tracking-[0.12em] border border-neutral-200 rounded-full px-3 py-1.5 text-neutral-600">
              {k} {fm(v || 0)}
            </span>
          ))}
          <span className="pb-mono text-[9.5px] uppercase tracking-[0.12em] rounded-full px-3 py-1.5" style={{ color: "#F59E0B", background: "#F59E0B1c" }}>
            Break-even {ec.breakEvenTickets ?? "—"} of {ec.sellable} paid
          </span>
          {ec.funding && (
            <span className="pb-mono text-[9.5px] uppercase tracking-[0.12em] rounded-full px-3 py-1.5" style={{ color: "#EF5350", background: "#EF53501c" }}>
              Peak funding {fm(ec.funding.amount)}
            </span>
          )}
        </div>

        <div className="mt-7">
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <p className="pb-mono text-[8.5px] uppercase tracking-[0.22em] text-neutral-400">Partner target coverage</p>
            <p className="pb-mono text-[10px] text-neutral-600">{fm(ec.partnerSigned)} signed · {fm(ec.partnerWeighted)} weighted · target {fm(ec.partnerTarget)}</p>
          </div>
          <div className="h-[5px] rounded-full overflow-hidden bg-neutral-200/70">
            <div className="h-full rounded-full" style={{ width: `${dekning}%`, background: "#F59E0B" }} />
          </div>
        </div>

        {(ec.risks || []).length > 0 && (
          <div className="mt-7 grid gap-2" data-testid="pb-economics-risks">
            {ec.risks.map((r, i) => (
              <p key={i} className="flex items-start gap-2.5 text-[13px] leading-snug" style={{ color: r.tone === "red" ? "#EF5350" : r.tone === "amber" ? "#B45309" : "#059669" }}>
                <span className="mt-[6px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "currentColor" }} />
                {r.text}
              </p>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
          {interaktiv && (
            <button type="button" onClick={() => nav("/admin/budsjett")} className="inline-flex items-center gap-1.5 pb-mono text-[10px] uppercase tracking-[0.16em] text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="pb-economics-link">
              Open Budget →
            </button>
          )}
          <p className="pb-mono text-[9px] uppercase tracking-[0.14em] text-neutral-400">Live from the Budget engine · NOK ex VAT · Internal only — never in the shared version</p>
        </div>
      </div>
    );
  };

  const RENDER = {
    vision: rVision, building: rBuilding, formula: rFormula, choices: rChoices,
    partnership: rPartnership, speakers: rSpeakers, economics: rEconomics, priorities: rPriorities,
    tasks: rTasks, milestones: rMilestones, success: rSuccess,
  };

  return (
    <div className={`flex gap-12 pb-rise ${delt ? "pb-delt" : ""}`} data-testid="playbook-dokument" onMouseMove={sporMus}>
      {/* Sticky innholdsfortegnelse */}
      <aside className="pb-toc hidden xl:block" data-testid="pb-toc">
        <p className="pb-mono text-[8.5px] uppercase tracking-[0.3em] text-neutral-400 pb-3">Contents</p>
        <div className="pb-fremdrift mb-4" data-testid="pb-fremdrift"><span style={{ width: `${fremdrift}%` }} /></div>
        {synlige.map((s, i) => (
          <button key={s.id} type="button" onClick={() => scrollTil(s.key)} className={`text-neutral-700 ${aktivSek === s.key ? "on" : ""}`} data-testid={`pb-toc-${s.key}`}>
            <span className="nr">{String(i + 1).padStart(2, "0")}</span>
            <span className="truncate">{s.title}</span>
          </button>
        ))}
      </aside>

      <div className="pb-doc flex-1 min-w-0">
        {/* Sticky kontekst-bar der TOC ikke vises */}
        <div className="pb-minibar xl:hidden" data-testid="pb-minibar">
          <span className="pb-mono text-[9.5px] uppercase tracking-[0.16em] text-neutral-500 truncate">
            {(() => {
              const i = synlige.findIndex((s) => s.key === aktivSek);
              return i >= 0 ? `${String(i + 1).padStart(2, "0")} · ${synlige[i].title}` : "Project Playbook";
            })()}
          </span>
          <div className="pb-fremdrift flex-1"><span style={{ width: `${fremdrift}%` }} /></div>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="text-neutral-400 hover:text-neutral-800 transition-colors shrink-0"
            aria-label="Til toppen"
          >
            <ArrowUp size={13} />
          </button>
        </div>

        {/* Masthead — redaksjonelt dokumenthode */}
        <div className="pb-hero">
          <div className="relative z-10">
            <div className="flex items-center justify-between gap-x-4 gap-y-3 flex-wrap">
              <p className="pb-kicker">{ev.name || "Norwegian AI Summit 2027"} — {delt ? "Private brief" : "Internal"}</p>
              {verktoy && <div className="hidden sm:block">{verktoy}</div>}
            </div>
            <h1 className="pb-tittel mt-5">Project Playbook</h1>
            <p className="pb-ingress mt-3.5">
              How we build {ev.name || "Norwegian AI Summit 2027"} — strategy, choices, economics and priorities, in one living document.
            </p>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2.5 mt-5" data-testid="pb-puls">
              <span className="pb-chip" data-testid="pb-puls-dager"><span className="pb-livedot" />Living · D–{dagerIgjen} to showtime</span>
              {apneValg > 0 && (
                <button type="button" className="pb-chip" style={{ color: "#F59E0B" }} onClick={() => scrollTil("choices")} data-testid="pb-puls-open">
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#F59E0B" }} />
                  {apneValg} open decision{apneValg > 1 ? "s" : ""}
                </button>
              )}
              {(data.priorities || []).length > 0 && (
                <button type="button" className="pb-chip" style={{ color: "#0EA5E9" }} onClick={() => scrollTil("priorities")} data-testid="pb-puls-pri">
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#0EA5E9" }} />
                  {data.priorities.length} priorities in motion
                </button>
              )}
            </div>

            <div className="pb-hair flex flex-wrap items-end justify-between gap-x-8 gap-y-5 mt-6 pt-4" data-testid="pb-meta">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-9 gap-y-4 min-w-0">
                {[
                  ["Event date", dato],
                  ["Venue", `${venue} · Bergen`],
                  ["Last updated", fmtDato(data.lastUpdated) || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <p className="pb-mono text-[8.5px] uppercase tracking-[0.24em] text-neutral-400">{k}</p>
                    <p className="text-[13px] font-medium mt-1.5 truncate text-neutral-900">{v}</p>
                  </div>
                ))}
              </div>
              {verktoy && <div className="sm:hidden">{verktoy}</div>}
            </div>
          </div>
        </div>

        {synlige.map((s, i) => (
          <Seksjon
            key={s.id}
            s={s}
            nr={String(i + 1).padStart(2, "0")}
            edit={edit}
            onPatch={(b) => onPatchSection(s.id, b)}
            onFlytt={(r) => onFlytt(s, r)}
            onKopier={onKopier ? () => onKopier(s.key) : null}
          >
            {RENDER[s.key] ? RENDER[s.key](s) : null}
          </Seksjon>
        ))}

        <p className="pb-mono text-[9px] uppercase tracking-[0.26em] text-neutral-400 border-t border-neutral-200 pt-6">
          {delt
            ? "Norwegian AI Summit 2027 · Private brief — please do not distribute"
            : "Living document · choices mirror Strategy Board · priorities mirror Saker"}
        </p>
        <div className="pb-signoff" aria-hidden="true">Norwegian AI Summit</div>
        <div className="pb-12" />
      </div>
    </div>
  );
}
